import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { PeriodMonthColumn } from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager-month';
import HotelPricingAssistantTableTooltipsTooltipLeftToSell, {
  type LeftToSellTooltip,
} from 'lumen/components/hotel/pricing-assistant/table/tooltips/tooltip-left-to-sell';
import type { Period } from 'lumen/models';
import {
  isOccupancyLoading,
  type LeftToSellValues,
  resolveLeftToSellValues,
} from 'lumen/utils/pricing-assistant/left-to-sell-utils';

interface HotelPricingAssistantTableCellsTdLeftToSellMonthSignature {
  Args: {
    column: PeriodMonthColumn;
    value: Period;
  };
}

export default class HotelPricingAssistantTableCellsTdLeftToSellMonth extends Component<HotelPricingAssistantTableCellsTdLeftToSellMonthSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service('pricing-assistant-data-request-service')
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  get displayValue(): number | string {
    return this.values.leftToSell ?? this.intl.t('general.missing');
  }

  /* The overbooking needs both restrictions requests, as the Rate groups cell does.
     Rooms have no overbooking pushes yet. */
  get isLoading(): boolean {
    const { restrictionsRecommendationsRequestTask, restrictionsRequestTask } =
      this.pricingAssistantDataRequestService;
    const isHotelOverbookingPushing =
      !this.isRoom && this.args.value.hasOverbookingActionInProgress;

    return (
      isOccupancyLoading(
        this.pricingAssistantDataRequestService,
        this.args.column.pmsRoomId,
      ) ||
      restrictionsRequestTask.isRunning ||
      restrictionsRecommendationsRequestTask.isRunning ||
      isHotelOverbookingPushing
    );
  }

  get isMissing(): boolean {
    return this.values.leftToSell == null;
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.value.fromDate);
  }

  get isRoom(): boolean {
    return this.args.column.pmsRoomId !== undefined;
  }

  get tooltip(): LeftToSellTooltip | null {
    const { leftToSell, overbooking } = this.values;

    if (leftToSell == null || overbooking == null) {
      return null;
    }

    return { leftToSell, overbooking, roomName: this.args.column.roomName };
  }

  get values(): LeftToSellValues {
    return resolveLeftToSellValues(this.args.value, this.args.column.pmsRoomId);
  }

  <template>
    {{#if this.isLoading}}
      <div class="loading-pulse" data-test-id="loading-pulse"></div>
    {{else}}
      {{! Focusable so keyboard users can open the tooltip }}
      <div
        class="mr-500
          {{if
            this.isPast
            'text-disabled'
            (if this.isMissing 'text-neutral-subdued')
          }}"
        data-test-id={{if
          this.isRoom
          "td-room-left-to-sell-month"
          "td-hotel-left-to-sell-month"
        }}
        tabindex={{if this.tooltip "0"}}
      >
        {{this.displayValue}}
        {{#if this.tooltip}}
          <HotelPricingAssistantTableTooltipsTooltipLeftToSell
            @date={{@value.fromDate}}
            @leftToSell={{this.tooltip.leftToSell}}
            @overbooking={{this.tooltip.overbooking}}
            @roomName={{this.tooltip.roomName}}
          />
        {{/if}}
      </div>
    {{/if}}
  </template>
}
